import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import { SurveyAnswerFormModel, SurveyAnswerFormResponse, SurveyWorkplaceOption, SurveyWorkplaceSelection } from '../model/survey';
import { SurveyWorkplaceSelectDialogComponent } from '../dialog/survey-workplace-select-dialog/survey-workplace-select-dialog.component';
import { GeneralYesNoDialogComponent } from '../dialog/general-yes-no-dialog/general-yes-no-dialog.component';

interface ScaleOption {
  value: number;
  label: string;
}

interface ScaleQuestion {
  no: number;
  text: string;
  key: string;
}

interface CheckboxOption {
  value: number;
  label: string;
}

interface CheckboxQuestion {
  no: number;
  text: string;
  key: string;
  options: CheckboxOption[];
}

@Component({
  selector: 'app-survey-answer',
  templateUrl: './survey-answer.component.html',
  styleUrl: './survey-answer.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ]
})
export class SurveyAnswerComponent implements OnInit {
  public readonly Constant = Constant;
  public showSpinner = true;
  private apiPath = '/survey/answer';
  public hasExistingAnswer = false;
  public isSubmitting = false;

  public targetMonth = '';

  // 048-SurveyAnswerPostが返しうる既知の業務エラー文言（回答期間外など）。
  // APIレスポンスの文字列を無条件に信頼せず、これらと一致する場合のみそのまま画面表示する
  private readonly knownSurveyAnswerErrors: string[] = [
    Constant.msgSurveyAnswerPeriodOver,
    Constant.msgSurveyInvalidAnswer,
    Constant.msgSurveyWorkplaceScopeRequired,
    Constant.msgSurveyWorkplaceRequired,
    Constant.msgSurveyPartDetailsRequired,
  ];

  public readonly scaleOptions: ScaleOption[] = [
    { value: 5, label: '非常にそう思う' },
    { value: 4, label: '' },
    { value: 3, label: 'どちらともいえない' },
    { value: 2, label: '' },
    { value: 1, label: '全くそう思わない' },
  ];

  public readonly participationScaleOptions: ScaleOption[] = [
    { value: 5, label: '非常に高い' },
    { value: 4, label: '' },
    { value: 3, label: '普通' },
    { value: 2, label: '' },
    { value: 1, label: '非常に低い' },
  ];

  public readonly selfQuestions: ScaleQuestion[] = [
    { no: 1, text: '今の仕事に前向きに取り組めている', key: 'q1' },
    { no: 2, text: '自分の仕事には意味・やりがいがあると感じる', key: 'q2' },
    { no: 3, text: '自分の仕事や活動には、自分で工夫・判断できる余地がある', key: 'q3' },
    { no: 4, text: '最近、自分が成長している実感がある', key: 'q4' },
    { no: 5, text: '自分の仕事や行動が、誰かや職場の役に立っている', key: 'q5' },
  ];

  public readonly workplaceQuestions: ScaleQuestion[] = [
    { no: 6, text: '業務量・内容は適正である', key: 'q6' },
    { no: 7, text: '公平性・納得感がある', key: 'q7' },
    { no: 8, text: '安心して意見を言える', key: 'q8' },
    { no: 9, text: '上司から必要な支援を得られている', key: 'q9' },
    { no: 10, text: '周囲との協力・支援関係がある', key: 'q10' },
    { no: 11, text: '努力や貢献が認識・承認されている', key: 'q11' },
    { no: 12, text: '無理なく働き続けられる見通しがある', key: 'q12' },
  ];

  public readonly q13No = 13;
  public readonly q13Text = '職場/店舗の課題';
  public readonly q13MaxSelect = 3;
  public q13Options: CheckboxOption[] = [];

  public readonly q14: CheckboxQuestion = {
    no: 14, text: '課題の改善を妨げている主な要因', key: 'q14',
    options: []
  };

  public readonly q15No = 15;
  public readonly q15Text = '職場の一番の課題の根拠';
  public q15Options: CheckboxOption[] = [];

  public readonly q16: CheckboxQuestion = {
    // 表示番号は18（職場についての設問の後ろ・組合についての設問の先頭に移したため）。保存キーは q16 のまま
    no: 18, text: 'この職場に対して、組合として今後優先すべき関わり方は何ですか', key: 'q16',
    options: []
  };

  public readonly yesNoOptions: CheckboxOption[] = [
    { value: 1, label: 'なし' },
    { value: 2, label: 'あり' },
    { value: 3, label: '把握していない' },
  ];

  public readonly q17No = 16; // 表示番号（保存キーは q17 のまま）
  public readonly q17Text = 'この1か月で、担当職場に退職者は出ましたか';
  public readonly q18No = 17; // 表示番号（保存キーは q18 のまま）
  public readonly q18Text = 'この1か月で、担当職場に休職者は出ましたか';

  public readonly q19No = 19;
  public readonly q19Text = '組合員の組合活動への参加意欲';

  public readonly q20: CheckboxQuestion = {
    no: 20, text: '組合関連課題', key: 'q20',
    options: []
  };

  public readonly q21: CheckboxQuestion = {
    no: 21, text: '組合員の生活への期待企画', key: 'q21',
    options: []
  };

  public answers: SurveyAnswerFormModel = {
    q1: null, q2: null, q3: null, q4: null, q5: null,
    q6: null, q7: null, q8: null, q9: null, q10: null, q11: null, q12: null,
    q13: [],
    q14: [],
    q15: null,
    q16: [],
    q17: null,
    q18: null,
    q19: null,
    q20: [],
    q21: [],
  };

  private workplaceOptions: SurveyWorkplaceOption[] = [];

  public workplaceSelection: SurveyWorkplaceSelection = {
    workplaceId: null,
    workplacePath: '',
    scopeType: null,
    partMemberCount: '',
    partName: '',
  };

  constructor(
    private title: Title,
    private router: Router,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private dialog: MatDialog,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleSurveyAnswer + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    this.loadAnswerForm();
  }

  private loadAnswerForm(): void {
    this.showSpinner = true;

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);

      this.commonUtil.apiGet(this.apiPath, options)
        .then(res => {
          const data: SurveyAnswerFormResponse = res.data;

          this.targetMonth = data.targetMonth;
          this.workplaceOptions = data.workplaceOptions;
          this.q13Options = data.questionOptions.survey_q13_issue;
          this.q14.options = data.questionOptions.survey_q14_hindrance;
          this.q15Options = data.questionOptions.survey_q15_basis;
          this.q16.options = data.questionOptions.survey_q16_priority;
          this.q20.options = data.questionOptions.survey_q20_issue;
          this.q21.options = data.questionOptions.survey_q21_expectation;

          if (data.existingAnswer) {
            this.hasExistingAnswer = true;
            const existing = data.existingAnswer;
            for (const key of Object.keys(this.answers)) {
              this.answers[key] = existing[key] ?? this.answers[key];
            }
            this.workplaceSelection = {
              workplaceId: existing.workplaceMasterId,
              workplacePath: existing.workplacePath ?? '',
              scopeType: existing.scopeType,
              partMemberCount: existing.partMemberCount != null ? String(existing.partMemberCount) : '',
              partName: existing.partName ?? '',
            };
          } else if (data.previousSelection) {
            // 未回答の当月分は、直近の回答月で選択していた職場/店舗をあらかじめ選択しておく
            const previous = data.previousSelection;
            this.workplaceSelection = {
              workplaceId: previous.workplaceMasterId,
              workplacePath: previous.workplacePath,
              scopeType: previous.scopeType,
              partMemberCount: previous.partMemberCount != null ? String(previous.partMemberCount) : '',
              partName: previous.partName ?? '',
            };
          }

          this.showSpinner = false;
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          this.showSpinner = false;
          if (err.data?.accessDenied) {
            this.router.navigate(['/error']);
          } else if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    });
  }

  onSelectWorkplace(): void {
    const dialogRef = this.dialog.open(SurveyWorkplaceSelectDialogComponent, {
      width: Constant.surveyWorkplaceDialogWidth,
      maxHeight: '650px',
      autoFocus: false,
      data: {
        options: this.workplaceOptions,
        selection: this.workplaceSelection,
      },
    });

    dialogRef.afterClosed().subscribe((result: SurveyWorkplaceSelection | undefined) => {
      if (result) {
        this.workplaceSelection = result;
      }
    });
  }

  isQ13Selected(value: number): boolean {
    return this.answers.q13.includes(value);
  }

  getQ13Priority(value: number): number | null {
    const idx = this.answers.q13.indexOf(value);
    return idx === -1 ? null : idx + 1;
  }

  isQ13Disabled(value: number): boolean {
    const full = this.answers.q13.length >= this.q13MaxSelect;
    return full && !this.isQ13Selected(value);
  }

  toggleQ13(value: number): void {
    const idx = this.answers.q13.indexOf(value);
    if (idx !== -1) {
      this.answers.q13.splice(idx, 1);
      return;
    }
    if (this.answers.q13.length < this.q13MaxSelect) {
      this.answers.q13.push(value);
    }
  }

  isChecked(key: string, value: number): boolean {
    return (this.answers[key] as number[]).includes(value);
  }

  toggleChecked(key: string, value: number): void {
    const arr = this.answers[key] as number[];
    const idx = arr.indexOf(value);
    if (idx !== -1) {
      arr.splice(idx, 1);
    } else {
      arr.push(value);
    }
  }

  private hasAllAnswers(): boolean {
    if (this.workplaceSelection.workplaceId == null) {
      return false;
    }
    const scaleKeys = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10', 'q11', 'q12', 'q15', 'q17', 'q18', 'q19'];
    if (scaleKeys.some(key => this.answers[key] == null)) {
      return false;
    }
    const arrayKeys = ['q13', 'q14', 'q16', 'q20', 'q21'];
    if (arrayKeys.some(key => (this.answers[key] as number[]).length === 0)) {
      return false;
    }
    return true;
  }

  isSubmitEnabled(): boolean {
    return !this.hasExistingAnswer && !this.isSubmitting && this.hasAllAnswers();
  }

  onCancel(): void {
    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        msg: Constant.msgSurveyAnswerCancelConfirm,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.router.navigate(['/survey']);
      }
    });
  }

  onSubmit(): void {
    if (!this.isSubmitEnabled()) {
      return;
    }
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        msg: Constant.msgSurveyAnswerSubmitConfirm,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.isSubmitting = true;

      Auth.currentSession().then(session => {
        const options = this.auth.createApiHeader(session);
        options['body'] = {
          workplaceMasterId: this.workplaceSelection.workplaceId,
          workplacePath: this.workplaceSelection.workplacePath,
          scopeType: this.workplaceSelection.scopeType,
          partMemberCount: this.workplaceSelection.partMemberCount,
          partName: this.workplaceSelection.partName,
          ...this.answers,
        };

        this.commonUtil.apiPost(this.apiPath, options)
          .then(() => {
            this.toastUtil.showInformationToast('', Constant.msgSurveyAnswerSuccess, Constant.toastShowMiliSec);
            this.router.navigate(['/survey']);
          })
          .catch(err => {
            this.commonUtil.debug().log(err);
            this.isSubmitting = false;
            if (err.message === Constant.msgLoginFailed) {
              this.commonUtil.showReloadDialog(this.auth);
            } else if (err.message === Constant.msgSurveyAlreadyAnswered) {
              // 回答自体はすでに成功していたケース（通信不良等での二重送信）。
              // 以後の回答操作は受け付けない
              this.hasExistingAnswer = true;
              this.toastUtil.showErrorToast('', Constant.msgSurveyAnsweredDisplay, Constant.toastShowMiliSec);
            } else if (this.knownSurveyAnswerErrors.includes(err.message)) {
              // バックエンドから既知の業務エラー文言が返っている場合
              // （回答期間外など）はそのまま表示する。
              // APIレスポンスの文字列を無条件に信頼しない
              // （enableHtml:trueのトーストへ渡すため）
              this.toastUtil.showErrorToast('', err.message, Constant.toastShowMiliSec);
            } else {
              // 通信断・タイムアウト等の場合は「Network Error」等の生の文言を出さず、専用の文言を表示する
              this.toastUtil.showErrorToast('', Constant.msgSurveyAnswerFailedRetry, Constant.toastShowMiliSec);
            }
          });
      });
    });
  }
}
